import { type ReactNode, type SVGAttributes, useId } from "react";

import { tv, type VariantProps } from "tailwind-variants";

import {
	Admin,
	Apps,
	ArrowDown,
	ArrowLeft,
	ArrowRight,
	ArrowUp,
	Ban,
	Bell,
	BellRing,
	BenchmarkApp,
	BenchmarkAppNoShadow,
	Book,
	BookDashed,
	Bookmark,
	Bot,
	Brain,
	BrainCog,
	BrainFull,
	Building,
	Calendar,
	CalendarCheck,
	CallAnalysisApp,
	CallAnalysisAppNoShadow,
	ChartColumn,
	ChartPie,
	Chatbot,
	ChatGpt,
	Check,
	CheckFull,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	ChevronUp,
	ChevronUpDown,
	Circle,
	CircleCheck,
	CircleDashed,
	CircleMinus,
	CirclePlus,
	CircleX,
	Clipboard,
	Clock,
	ClosePanel,
	Cloud,
	CodeXml,
	Cog,
	Compress,
	Copy,
	Cross,
	Crown,
	Cube,
	Diamond,
	Diff,
	DocumentAnalysisApp,
	DocumentAnalysisAppNoShadow,
	DocumentValidationApp,
	DocumentValidationAppNoShadow,
	Download,
	Dropbox,
	DueDiligenceApp,
	DueDiligenceAppNoShadow,
	EllipsisVertical,
	Envelope,
	Excel,
	Expand,
	ExternalLink,
	Eye,
	EyeClosed,
	File,
	FileFull,
	Files,
	FileSymLink,
	FileText,
	Filter,
	Folder,
	FolderOpen,
	Forward,
	GenericApp,
	GenericAppNoShadow,
	Glasses,
	Globe,
	GoNoGoApp,
	GoNoGoAppNoShadow,
	Google,
	GoogleDrive,
	HandPointer,
	HardDrive,
	Heading,
	History,
	Hourglass,
	Image,
	Info,
	Layers,
	Letter,
	Lightbulb,
	Link,
	List,
	ListCheck,
	ListOrdered,
	Lock,
	LockOpen,
	Logout,
	Markdown,
	MemoDraftingApp,
	MemoDraftingAppNoShadow,
	Menu,
	Message,
	Microsoft,
	Milestone,
	Minus,
	Move,
	MoveDiagonal,
	Onedrive,
	Openai,
	OpenPanel,
	Palette,
	Paperclip,
	PaperPlane,
	Pause,
	Pdf,
	Pen,
	Pin,
	Play,
	Plug,
	PlugZap,
	Plus,
	Podcast,
	Pptx,
	Presentation,
	ProposalApp,
	ProposalAppNoShadow,
	Puzzle,
	QuestionnairesApp,
	QuestionnairesAppNoShadow,
	QuoteLeft,
	Redo,
	Refresh,
	Reply,
	ResearchApp,
	ResearchAppNoShadow,
	Retry,
	Save,
	ScanSearch,
	Search,
	Sharepoint,
	Shield,
	ShieldCheck,
	ShieldOff,
	Sitemap,
	SortDownLetters,
	SortDownNumbers,
	SortUpLetters,
	SortUpNumbers,
	Sparkles,
	Spinner,
	Square,
	SquarePen,
	Star,
	StarFilled,
	Support,
	Table,
	Terminal,
	TextType,
	ThumbDown,
	ThumbUp,
	Trash,
	TriangleAlert,
	Undo,
	Unpin,
	Unplug,
	Upload,
	UploadFile,
	User,
	UserCog,
	Users,
	UsersFull,
	Video,
	WandSparkles,
	Word,
	Youtube,
	Zap,
} from "@virtual-brain-lib/frontend-assets";
import { cn } from "@virtual-brain-lib/frontend-design-system/utils";

import { Tooltip, type TooltipProps } from "./Tooltip";

export interface IconBaseProps extends SVGAttributes<SVGElement> {
	children?: ReactNode;
	size?: string | number;
	color?: string;
	title?: string;
}
export type IconType = (props: IconBaseProps) => ReactNode;

export const ICONS = {
	eye: Eye,
	file: File,
	message: Message,
	chatbot: Chatbot,
	diff: Diff,
	x: Cross,
	brain: Brain,
	brainFull: BrainFull,
	questionCircle: Support,
	signOutAlt: Logout,
	calendarCheck: CalendarCheck,
	cog: Cog,
	ellipsisV: EllipsisVertical,
	infoCircle: Info,
	building: Building,
	user: User,
	userCog: UserCog,
	users: Users,
	spinner: Spinner,
	check: Check,
	pen: Pen,
	trash: Trash,
	thumbUp: ThumbUp,
	thumbDown: ThumbDown,
	paperPlane: PaperPlane,
	paperclip: Paperclip,
	plus: Plus,
	calendar: Calendar,
	bell: Bell,
	bellRing: BellRing,
	square: Square,
	regSquare: Diamond,
	star: Star,
	starFilled: StarFilled,
	play: Play,
	chevronLeft: ChevronLeft,
	chevronRight: ChevronRight,
	lock: Lock,
	lockOpen: LockOpen,
	exclamationTriangle: TriangleAlert,
	cube: Cube,
	chatGpt: Openai,
	adminPanel: Admin,
	retry: Retry,
	pin: Pin,
	unpin: Unpin,
	copy: Copy,
	eyeClosed: EyeClosed,
	share: Forward,
	files: Files,
	circleCheck: CircleCheck,
	circleDashed: CircleDashed,
	globe: Globe,
	clipboard: Clipboard,
	bookDashed: BookDashed,
	arrowDown: ArrowDown,
	crown: Crown,
	zap: Zap,
	hourglassHalf: Hourglass,
	uploadFile: UploadFile,
	stack: Layers,
	chevronDown: ChevronDown,
	fileSimLink: FileSymLink,
	save: Save,
	"google-drive": GoogleDrive,
	sharepoint: Sharepoint,
	onedrive: Onedrive,
	dropbox: Dropbox,
	penSquare: SquarePen,
	forbidden: Ban,
	circleMinus: CircleMinus,
	circle: Circle,
	listCheck: ListCheck,
	fileAlt: FileText,
	cloud: Cloud,
	puzzle: Puzzle,
	update: Refresh,
	glasses: Glasses,
	pie: ChartPie,
	circlePlus: CirclePlus,
	letterSortDown: SortDownLetters,
	letterSortUp: SortUpLetters,
	numberSortDown: SortDownNumbers,
	numberSortUp: SortUpNumbers,
	book: Book,
	magnifyingGlass: Search,
	chatGpt2: ChatGpt,
	lightbulb: Lightbulb,
	chevronUp: ChevronUp,
	bookmark: Bookmark,
	download: Download,
	brainCog: BrainCog,
	word: Word,
	excel: Excel,
	pdf: Pdf,
	pptx: Pptx,
	presentation: Presentation,
	reply: Reply,
	plug: Plug,
	pause: Pause,
	unplug: Unplug,
	robot: Bot,
	apps: Apps,
	palette: Palette,
	openPanel: OpenPanel,
	closePanel: ClosePanel,
	menu: Menu,
	arrowLeft: ArrowLeft,
	arrowRight: ArrowRight,
	externalLink: ExternalLink,
	folderOpen: FolderOpen,
	quoteLeft: QuoteLeft,
	chevronUpDown: ChevronUpDown,
	filter: Filter,
	upload: Upload,
	link: Link,
	arrowUp: ArrowUp,
	clock: Clock,
	codeXml: CodeXml,
	circleX: CircleX,
	scanSearch: ScanSearch,
	shieldOff: ShieldOff,
	shieldCheck: ShieldCheck,
	shield: Shield,
	plugZap: PlugZap,
	markdown: Markdown,
	envelop: Envelope,
	sitemap: Sitemap,
	table: Table,
	terminal: Terminal,
	handPointer: HandPointer,
	wandSparkles: WandSparkles,
	podcast: Podcast,
	image: Image,
	folder: Folder,
	youtube: Youtube,
	video: Video,
	letter: Letter,
	hardDrive: HardDrive,
	checkFull: CheckFull,
	fileFull: FileFull,
	usersFull: UsersFull,
	milestone: Milestone,
	compress: Compress,
	expand: Expand,
	callAnalysisApp: CallAnalysisApp,
	documentAnalysisApp: DocumentAnalysisApp,
	documentValidationApp: DocumentValidationApp,
	dueDiligenceApp: DueDiligenceApp,
	goNoGoApp: GoNoGoApp,
	memoDraftingApp: MemoDraftingApp,
	genericApp: GenericApp,
	proposalApp: ProposalApp,
	questionnairesApp: QuestionnairesApp,
	researchApp: ResearchApp,
	benchmarkApp: BenchmarkApp,
	callAnalysisAppNS: CallAnalysisAppNoShadow,
	documentAnalysisAppNS: DocumentAnalysisAppNoShadow,
	documentValidationAppNS: DocumentValidationAppNoShadow,
	dueDiligenceAppNS: DueDiligenceAppNoShadow,
	goNoGoAppNS: GoNoGoAppNoShadow,
	memoDraftingAppNS: MemoDraftingAppNoShadow,
	genericAppNS: GenericAppNoShadow,
	proposalAppNS: ProposalAppNoShadow,
	questionnairesAppNS: QuestionnairesAppNoShadow,
	researchAppNS: ResearchAppNoShadow,
	benchmarkAppNS: BenchmarkAppNoShadow,
	undo: Undo,
	redo: Redo,
	history: History,
	google: Google,
	microsoft: Microsoft,
	sparkles: Sparkles,
	list: List,
	listOrdered: ListOrdered,
	heading: Heading,
	textType: TextType,
	minus: Minus,
	move: Move,
	moveDiagonal: MoveDiagonal,
	chartColumn: ChartColumn,
} satisfies Record<string, IconType>;

export type Icons = keyof typeof ICONS;

export const iconVariants = tv({
	base: "shrink-0 focus-within:outline-0",
	variants: {
		size: {
			xs: "size-2.5",
			sm: "size-3",
			md: "size-4",
			lg: "size-5",
			xl: "size-6",
			"2xl": "size-8",
			"3xl": "size-10",
			"4xl": "size-12",
			full: "h-auto w-full content-center-safe",
		},
		color: {
			default: "text-default",
			defaultLight: "text-default/65",
			primary: "text-on-primary",
			success: "text-on-success",
			warning: "text-warning",
			danger: "text-on-danger",
			info: "text-on-info",
			white: "text-white",
			black: "text-black",
			gray: "text-on-primary/70",
			inactive: "text-on-primary/50",
		},
	},
});

type IconVariant = VariantProps<typeof iconVariants>;

export type IconProps = IconVariant & {
	readonly name: Icons;
	readonly className?: string;
	readonly tooltip?: Omit<TooltipProps, "anchor">;
};

export function Icon({ name, size, color, tooltip, className }: IconProps) {
	const tooltipId = useId();
	const IconRender = ICONS[name];
	const iconClassName = iconVariants({
		size,
		color,
		className: cn(className, name === "spinner" && "animate-spin"),
	});

	return (
		<>
			<IconRender data-tooltip={tooltip ? tooltipId : undefined} className={iconClassName} />
			{tooltip && <Tooltip anchor={tooltipId} {...tooltip} />}
		</>
	);
}
